import React, { useCallback, useMemo, useRef, useState } from "react";
import { useSelector, useDispatch } from "react-redux";

// material-ui
import { makeStyles } from "@mui/styles";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";

// config
import { configTable, menuOptions } from "./config";

// Styles
import { ColumnContainer, styles } from "./styles";

// Actions creators
import {
  loadFiles,
  clearFiles,
  setConfigFilter,
  setConfigPage,
  setConfigPageSize,
  setConfigSorted,
} from "redux/ducks/pricesSection";
import CardTable from "components/CardTable/CardTable";
import { PricesFilters } from "views/PricesSection/PricesFilters";
import { PricesIcon } from "components/PricesIcon/PricesIcon";
import { getDecimals } from "helpers/getNumberFormats";
import CustomDropdown from "components/CustomDropdown/CustomDropdown.js";
import PricesModals from "components/PricesModals/PricesModals";
import moment from "moment";
import defaultImage from "assets/img/image_placeholder.jpg";
import SuccessImage from "assets/img/checked.png";
const useStyles = makeStyles(styles);

const dropdownTypes = {
  [menuOptions.loadPrices]: {
    name: "Subir precios",
    url: "prices/cre",
    formData: "prices",
  },
  [menuOptions.loadCosts]: {
    name: "Subir costos",
    url: "prices/costs",
    formData: "costs",
  },
};

export default function Prices() {
  const classes = useStyles();
  const dispatch = useDispatch();
  // local state
  const [startDate, setStartDate] = useState(moment());
  const [endDate, setEndDate] = useState(moment());
  const [dropdown, setDropdown] = useState(
    dropdownTypes[menuOptions.loadPrices]
  );
  const [modal, setModal] = useState(false);

  // selectors
  const prices = useSelector((state) => state.pricesSection.prices);
  const isLoading = useSelector((state) => state.pricesSection.isLoading);
  const { isLoadingFiles, inserted, ignored, showLast } = useSelector(
    (state) => state.pricesSection.files
  );
  const { filtered, page, pageSize, sorted } = useSelector(
    (state) => state.pricesSection.config
  );
  const [file, setFile] = React.useState(null);
  const [imagePreviewUrl, setImagePreviewUrl] = React.useState(defaultImage);
  const fileInput = useRef(null);

  // Handlers
  const handleDropdownItem = useCallback((event) => {
    setDropdown(dropdownTypes[event]);
    setModal(true);
  }, []);
  const handleCloseModal = useCallback(() => {
    setModal(false);
    dispatch(clearFiles());
  }, []);
  const handleSubmit = useCallback(() => {
    const startDateFormat = startDate.format("YYYY-MM-DD");
    const endDateFormat = endDate.format("YYYY-MM-DD");
    dispatch(
      loadFiles(
        file,
        dropdown.url,
        startDateFormat,
        endDateFormat,
        dropdown.formData
      )
    );
  }, [file, dropdown.url, startDate, endDate, dropdown.formData]);
  const handleImageChange = useCallback((e) => {
    e.preventDefault();
    let reader = new FileReader();
    let file = e.target.files[0];
    reader.onloadend = () => {
      setFile(file);
      setImagePreviewUrl(SuccessImage);
    };
    reader.readAsDataURL(file);
  }, []);
  const handleClick = useCallback(() => {
    fileInput.current.click();
  }, []);
  const handleRemove = useCallback(() => {
    setFile(null);
    setImagePreviewUrl(defaultImage);
    fileInput.current.value = null;
  }, []);

  const handleChangeFilteredMemoized = useCallback(
    (event) => {
      dispatch(setConfigFilter(event));
    },
    []
  );
  const handleChangePageMemoized = useCallback(
    (event) => {
      dispatch(setConfigPage(event));
    },
    []
  );
  const handleChangePageSizeMemoized = useCallback(
    (event) => {
      dispatch(setConfigPageSize(event));
    },
    []
  );
  const handleChangeSortedMemoized = useCallback(
    (event) => {
      dispatch(setConfigSorted(event));
    },
    []
  );

  const columns = useMemo(() => [
    ...configTable,
    {
      Header: "Tope KGs",
      accessor: "kiloPrice",
      filterable: true,
      filterMethod: (filter, row) =>
        String(row.kiloPrice)
          .toLowerCase()
          .includes(filter.value.toLowerCase()),
      Cell: ({ value }) => {
        return <ColumnContainer>{getDecimals(value, 2)}</ColumnContainer>;
      },
    },
    {
      Header: "Tope LTS",
      accessor: "literPrice",
      filterable: true,
      filterMethod: (filter, row) =>
        String(row.literPrice)
          .toLowerCase()
          .includes(filter.value.toLowerCase()),
      Cell: ({ value }) => {
        return <ColumnContainer>{getDecimals(value, 2)}</ColumnContainer>;
      },
    },
    {
      Header: "Costo",
      accessor: "cost",
      filterable: true,
      filterMethod: (filter, row) =>
        row.cost
          ? String(row.cost)
            .toLowerCase()
            .includes(filter.value.toLowerCase())
          : "",
      Cell: ({ value }) => {
        return <ColumnContainer>{getDecimals(value, 2)}</ColumnContainer>;
      },
    },
    {
      Header: "Margen Anterior",
      accessor: "lastProfit",
      filterable: true,
      filterMethod: (filter, row) =>
        row.lastProfit
          ? String(row.lastProfit)
            .toLowerCase()
            .includes(filter.value.toLowerCase())
          : "",
      Cell: ({ value }) => {
        return <ColumnContainer>{getDecimals(value, 2)}</ColumnContainer>;
      },
    },
    {
      Header: "Margen Actual",
      accessor: "profit",
      filterable: true,
      filterMethod: (filter, row) =>
        row.profit
          ? String(row.profit)
            .toLowerCase()
            .includes(filter.value.toLowerCase())
          : "",
      Cell: ({ value }) => {
        return <ColumnContainer>{getDecimals(value, 2)}</ColumnContainer>;
      },
    },
    {
      Header: "Semana",
      accessor: "week",
      filterable: true,
      filterMethod: (filter, row) =>
        String(row.week)
          .toLowerCase()
          .includes(filter.value.toLowerCase()),
      Cell: ({ value }) => {
        return <ColumnContainer>{value}</ColumnContainer>;
      },
    },
    {
      Header: "Tendencia",
      accessor: "grow",
      filterable: false,
      Cell: (props) => (
        <ColumnContainer justify="center">
          <PricesIcon grow={props.original.grow} />
        </ColumnContainer>
      ),
    },
  ].map((col) => {
    return {
      ...col,
      headerClassName: `${classes.thTable}`,
    };
  }), [classes.thTable]);

  return (
    <React.Fragment>
      <CardTable
        data={prices}
        columns={columns}
        loading={isLoading}
        filtered={filtered}
        page={page}
        sorted={sorted}
        pageSize={pageSize}
        previousText="Anterior"
        nextText="Siguiente"
        noDataText="Sin datos"
        pageText="Pagina"
        ofText="de"
        resizable={false}
        rowsText="precios"
        onPageChange={handleChangePageMemoized}
        onPageSizeChange={handleChangePageSizeMemoized}
        onSortedChange={handleChangeSortedMemoized}
        onFilteredChange={handleChangeFilteredMemoized}
        tableContent={
          <PricesFilters>
            <CustomDropdown
              dropup
              buttonText="Subir archivos "
              hoverColor="info"
              buttonIcon={CloudUploadIcon}
              justify="end"
              buttonProps={{
                round: true,
                color: "info",
              }}
              onClick={(event) => handleDropdownItem(event)}
              dropdownList={[menuOptions.loadPrices, menuOptions.loadCosts]}
            />
          </PricesFilters>
        }
      />
      <PricesModals
        startDate={startDate}
        onChangeStartDate={setStartDate}
        endDate={endDate}
        onChangeEndDate={setEndDate}
        onClose={handleCloseModal}
        isOpen={modal}
        title={dropdown.name}
        file={file}
        isLoading={isLoadingFiles}
        imagePreviewUrl={imagePreviewUrl}
        fileInput={fileInput}
        onImageChange={handleImageChange}
        onClick={handleClick}
        onRemove={handleRemove}
        onSubmit={handleSubmit}
        inserted={inserted}
        ignored={ignored.length}
        showLast={showLast}
      />
    </React.Fragment>
  );
}
